import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import blogPosts from './blogData';
import '../css/BlogPage.css';

const setMetaTag = (attr, key, value) => {
  if (!value) return;
  let tag = document.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
};

const BlogPage = () => {
  const [query, setQuery] = useState('');
  const [tag, setTag] = useState('All');

  const sorted = useMemo(
    () =>
      [...blogPosts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    []
  );

  const tags = useMemo(() => {
    const values = new Set();
    sorted.forEach((post) => post.tags.forEach((item) => values.add(item)));
    return ['All', ...Array.from(values)];
  }, [sorted]);

  const filtered = useMemo(() => {
    const lower = query.trim().toLowerCase();
    return sorted.filter((post) => {
      const matchesTag = tag === 'All' || post.tags.includes(tag);
      const matchesQuery =
        !lower ||
        post.title.toLowerCase().includes(lower) ||
        post.excerpt.toLowerCase().includes(lower);
      return matchesTag && matchesQuery;
    });
  }, [sorted, tag, query]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  const latestDate = sorted[0]?.date ? new Date(sorted[0].date).toLocaleDateString() : '—';

  React.useEffect(() => {
    const title = 'BlinkURL Blog — Product updates and company news';
    document.title = title;
    const description =
      'Official BlinkURL updates on product launches, security improvements, and platform reliability.';
    setMetaTag('name', 'description', description);
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:url', `${window.location.origin}/blog`);
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);
  }, []);

  return (
    <div className="blog-shell">
      <header className="blog-hero">
        <div>
          <span className="blog-kicker">BlinkURL Updates</span>
          <h1>Inside BlinkURL</h1>
          <p>
            The official blog for product releases, security improvements, and platform updates.
          </p>
        </div>
        <div className="blog-hero-panel">
          <div>
            <span>Posts</span>
            <strong>{sorted.length}</strong>
          </div>
          <div>
            <span>Latest</span>
            <strong>{latestDate}</strong>
          </div>
          <div>
            <span>Topics</span>
            <strong>{tags.length - 1}</strong>
          </div>
        </div>
      </header>

      <section className="blog-controls">
        <input
          type="search"
          placeholder="Search updates..."
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <div className="blog-tags">
          {tags.map((value) => (
            <button
              type="button"
              key={value}
              className={`blog-tag ${tag === value ? 'active' : ''}`}
              onClick={() => setTag(value)}
            >
              {value}
            </button>
          ))}
        </div>
      </section>

      {featured ? (
        <section className={`blog-featured theme-${featured.theme}`}>
          <div className="blog-featured-body">
            <div className="blog-meta">
              <span>{new Date(featured.date).toLocaleDateString()}</span>
              <span>{featured.readTime}</span>
            </div>
            <h2>{featured.title}</h2>
            <p>{featured.excerpt}</p>
            <div className="blog-tag-row">
              {featured.tags.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
            <Link to={`/blog/${featured.slug}`} className="blog-cta">
              Read the update
            </Link>
          </div>
          <div className="blog-featured-card">
            <span className="label">Featured</span>
            <strong>{featured.category}</strong>
            <small>{featured.author}</small>
          </div>
        </section>
      ) : (
        <div className="blog-empty">No updates match your filters.</div>
      )}

      {rest.length > 0 && (
        <section className="blog-grid">
          {rest.map((post) => (
            <article key={post.slug} className={`blog-card theme-${post.theme}`}>
              <div className="blog-card-top">
                <div>
                  <span>{post.category}</span>
                  <strong>{post.readTime}</strong>
                </div>
                <small>{new Date(post.date).toLocaleDateString()}</small>
              </div>
              <h3>{post.title}</h3>
              <p>{post.excerpt}</p>
              <div className="blog-card-tags">
                {post.tags.slice(0, 3).map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
              <Link to={`/blog/${post.slug}`}>Read more</Link>
            </article>
          ))}
        </section>
      )}
    </div>
  );
};

export default BlogPage;
